import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { Configuration, ConfigurationsService } from '@ubie/core';
import { ConfigurationPopoverComponent } from '../configuration-popover/configuration-popover.component';

type PopoverAction = 'accept' | 'refuse' | 'install';

const ACTION_ACCEPT: PopoverAction = 'accept';
const ACTION_REFUSE: PopoverAction = 'refuse';
const ACTION_INSTALL: PopoverAction = 'install';
const POPOVER_ACTIONS: readonly PopoverAction[] = [
  ACTION_ACCEPT,
  ACTION_REFUSE,
  ACTION_INSTALL,
];

@Component({
  selector: 'acl-configurations-by-devices-list-item',
  standalone: false,
  templateUrl: './configurations-by-devices-list-item.component.html',
  styleUrl: './configurations-by-devices-list-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfigurationsByDevicesListItemComponent {
  private readonly configurationsService = inject(ConfigurationsService);
  private readonly popoverController = inject(PopoverController);

  readonly configuration = input.required<Configuration>();

  readonly canOpen = computed(() => {
    const config = this.configuration();
    return !config.pending && config.accepted && !config.installed;
  });

  readonly statusClass = computed(() => {
    const config = this.configuration();
    if (config.pending) {
      return 'pending';
    }
    if (config.accepted) {
      return 'accepted';
    }
    return 'refused';
  });

  async showPopOver(ev: Event): Promise<void> {
    const configuration = this.configuration();
    const popover = await this.popoverController.create({
      component: ConfigurationPopoverComponent,
      componentProps: { configuration },
      event: ev,
      translucent: true,
    });

    await popover.present();

    const { data } = await popover.onDidDismiss();
    if (!this.isPopoverAction(data)) {
      return;
    }

    this.applyLocalStateUpdate(data, configuration);
    const request$ = this.getActionRequest(data, configuration);
    request$?.subscribe({ error: () => undefined });
  }

  private isPopoverAction(value: unknown): value is PopoverAction {
    return POPOVER_ACTIONS.includes(value as PopoverAction);
  }

  private applyLocalStateUpdate(
    action: PopoverAction,
    configuration: Configuration,
  ): void {
    if (action === ACTION_ACCEPT) {
      configuration.pending = false;
      configuration.accepted = true;
      configuration.acceptedAt = new Date();
      return;
    }

    if (action === ACTION_REFUSE) {
      configuration.pending = false;
      configuration.accepted = false;
      configuration.acceptedAt = new Date();
    }
  }

  private getActionRequest(
    action: PopoverAction,
    configuration: Configuration,
  ) {
    if (action === ACTION_ACCEPT) {
      return this.configurationsService.acceptConfiguration(configuration);
    }

    if (action === ACTION_REFUSE) {
      return this.configurationsService.refuseConfiguration(configuration);
    }

    if (action === ACTION_INSTALL) {
      return this.configurationsService.installConfiguration(
        configuration,
        true,
      );
    }

    return undefined;
  }
}
